import React, { useEffect, useState } from 'react';
import { getUserCases, getAppUser, getCookie } from '../../functions/utils';
import {
  getBestandImports,
  getBestandFiles,
  createBestandImport,
  deleteBestandImport,
  updateBestandImportStamp,
  handleBestandImport,
  resetBestandStatus,
} from '../../functions/bestand';
import '../../css/ImportComponent.css';
import ImportHeaderComponent from '../page/ImportHeaderComponent';
import DropdownComponent from '../utilities/DropdownComponent';
import LoaderComponent from '../utilities/LoaderComponent';
import FileDropComponent from '../utilities/FileDropComponent';
import ConfirmationComponent from '../utilities/ConfirmationComponent';
import BestandLogo from '../../images/BestandLogo.png';
import { useTranslations } from '../utilities/TranslationContext';

import { library } from '@fortawesome/fontawesome-svg-core';
import { faXmark, faChevronDown, faArrowLeft, faCircleCheck, faCircleXmark, faEllipsisV } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

library.add(faChevronDown, faXmark, faArrowLeft, faCircleCheck, faCircleXmark, faEllipsisV);

const BestandComponent = () => {
  const { getTranslation, language } = useTranslations();

  const [userCases, setUserCases] = useState([]);
  const [imports, setImports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Create form
  const [showEditor, setShowEditor] = useState(false);
  const [selectedCase, setSelectedCase] = useState(false);
  const [selectedYear, setSelectedYear] = useState(String(new Date().getFullYear()));
  const [jrnid, setJrnid] = useState('');
  const [accid, setAccid] = useState('');

  // Per-config upload state: { configId, files, isLoading, results }
  const [activeUpload, setActiveUpload] = useState(null);

  // Confirm delete
  const [confirmDeleteId, setConfirmDeleteId] = useState(false);

  // Results panel toggle
  const [resultsExpanded, setResultsExpanded] = useState(true);

  // Previously uploaded files per config (keyed by config id)
  const [expandedConfigId, setExpandedConfigId] = useState(null);
  const [configFiles, setConfigFiles] = useState({});

  useEffect(() => { init(); }, []);

  async function init() {
    const appUser = await getAppUser();
    if (!appUser) return setTimeout(init, 300);
    const [cases, imps] = await Promise.all([getUserCases(), getBestandImports()]);
    if (cases) setUserCases(cases);
    setImports(Array.isArray(imps) ? imps : []);
    setIsLoading(false);
  }

  async function reload() {
    const imps = await getBestandImports();
    setImports(Array.isArray(imps) ? imps : []);
  }

  // ── Create ──────────────────────────────────────────────────────────────────

  function toggleEditor() {
    setShowEditor(v => !v);
    setSelectedCase(false);
    setSelectedYear(String(new Date().getFullYear()));
    setJrnid('');
    setAccid('');
  }

  async function handleCreate() {
    if (!selectedCase || !selectedYear || !jrnid.trim() || !accid.trim()) {
      alert('Zorg dat alle velden gevuld zijn!');
      return;
    }
    const entiteit = `${selectedCase}_1`;
    const exists = imports.some(i => i.entiteit === entiteit && i.year === selectedYear && i.jrnid === jrnid && i.accid === accid);
    if (exists) { alert('Deze configuratie bestaat al!'); return; }

    await createBestandImport({ entiteit, year: selectedYear, jrnid: jrnid.trim(), accid: accid.trim() });
    toggleEditor();
    await reload();
  }

  // ── Upload ───────────────────────────────────────────────────────────────────

  function openUpload(config) {
    setActiveUpload({ config, files: [], isLoading: false, results: [] });
  }

  function closeUpload() {
    setActiveUpload(null);
  }

  async function runImport() {
    const { config, files } = activeUpload;
    setActiveUpload(u => ({ ...u, isLoading: true, results: [] }));
    await resetBestandStatus();

    const newResults = [];
    for (const file of files) {
      let xmlContent;
      try { xmlContent = await file.text(); }
      catch { newResults.push({ file: file.name, success: false, error: 'Kan bestand niet lezen' }); continue; }

      try {
        const result = await handleBestandImport({
          entiteit: config.entiteit,
          year: config.year,
          jrnid: config.jrnid,
          accid: config.accid,
          fileName: file.name,
          xmlContent,
        });
        newResults.push({ file: file.name, success: true, ...result });
      } catch (e) {
        newResults.push({ file: file.name, success: false, error: e.message });
      }
    }

    const anySuccess = newResults.some(r => r.success);
    if (anySuccess) await updateBestandImportStamp(config.id);

    setActiveUpload(u => ({ ...u, isLoading: false, results: newResults, files: [] }));
    setResultsExpanded(true);
    await reload();

    if (anySuccess) {
      const freshImps = await getBestandImports();
      const freshConfig = freshImps.find(i => i.id === config.id);
      if (freshConfig) {
        await refreshConfigFiles(freshConfig);
        setExpandedConfigId(freshConfig.id);
      }
    }
  }

  // ── Delete ───────────────────────────────────────────────────────────────────

  async function handleDelete(id) {
    await deleteBestandImport(id);
    setConfirmDeleteId(false);
    await reload();
  }

  // ── Config file history ───────────────────────────────────────────────────────

  async function toggleConfigFiles(imp) {
    if (expandedConfigId === imp.id) {
      setExpandedConfigId(null);
      return;
    }
    setExpandedConfigId(imp.id);
    if (!configFiles[imp.id] && imp.impid > 0) {
      const files = await getBestandFiles(imp.id);
      setConfigFiles(prev => ({ ...prev, [imp.id]: files }));
    }
  }

  async function refreshConfigFiles(imp) {
    if (imp.impid > 0) {
      const files = await getBestandFiles(imp.id);
      setConfigFiles(prev => ({ ...prev, [imp.id]: files }));
    }
  }

  // ── Helpers ──────────────────────────────────────────────────────────────────

  function getCaseName(entiteit) {
    const id = entiteit.substring(0, entiteit.indexOf('_'));
    const c = userCases.find(uc => String(uc.id) === String(id));
    return c ? c.name : entiteit;
  }

  function formatStamp(stamp) {
    if (!stamp) return '-';
    return new Date(stamp).toLocaleString(`${language?.language_code}-${language?.country_code}`, {
      year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric',
    });
  }

  const canCreate = selectedCase && selectedYear && jrnid.trim() && accid.trim();
  const canImport = activeUpload && activeUpload.files.length > 0 && !activeUpload.isLoading;

  return (
    <div className="import-container">
      <ImportHeaderComponent
        title="Bestand"
        logo={BestandLogo}
        subtitle={getTranslation('import_BestandSubtitle')}
      />
      <div className="import-component-container">
        <div className="import-component-content">
          <div className="import-content">

            {/* ── Create form ── */}
            {showEditor && (
              <div className="import-create-basic">
                <div className="case-content">
                  <p>{getTranslation('import_FVCase')}*</p>
                  <DropdownComponent
                    options={userCases.map(c => ({ label: c.name, value: c.id, type: 'case' }))}
                    defaultValue={{ label: getTranslation('import_SelectCase'), value: null, type: null }}
                    onSelect={o => o.type === 'case' && setSelectedCase(o.value)}
                    className="case"
                  />
                </div>
                <div className="jaar-content">
                  <p>{getTranslation('import_DropdownYear')}*</p>
                  <input
                    type="number"
                    min="1000"
                    max="9999"
                    className="jaar-input"
                    value={selectedYear}
                    onChange={e => setSelectedYear(e.target.value)}
                  />
                </div>
                <div className="administration-content">
                  <p>Dagboek (jrnid)*</p>
                  <input
                    type="text"
                    className="jaar-input"
                    placeholder="bijv. BANK"
                    value={jrnid}
                    onChange={e => setJrnid(e.target.value)}
                  />
                </div>
                <div className="administration-content">
                  <p>Bankrekening (accid)*</p>
                  <input
                    type="text"
                    className="jaar-input"
                    placeholder="bijv. 1010"
                    value={accid}
                    onChange={e => setAccid(e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* ── Menubar ── */}
            <div className="import-menubar">
              {!showEditor ? (
                <button className="import-btn new-btn hover" onClick={toggleEditor}>
                  {getTranslation('import_CreateNew')}
                </button>
              ) : (
                <>
                  <button className="import-btn submit-btn hover" disabled={!canCreate} onClick={handleCreate}>
                    {getTranslation('import_Submit')}
                  </button>
                  <button className="import-btn cancel-btn hover" onClick={toggleEditor}>
                    {getTranslation('import_Cancel')}
                  </button>
                </>
              )}
            </div>

            {/* ── Config list ── */}
            <LoaderComponent isLoading={isLoading} paddingTop="20px" />

            {!isLoading && imports.length > 0 && (
              <div className="list-content">
                <table border="1">
                  <tbody>
                    <tr className="list-header">
                      <td className="list-expand"></td>
                      <td className="list-case">{getTranslation('import_Case')}</td>
                      <td className="list-year">{getTranslation('import_Year')}</td>
                      <td>Dagboek</td>
                      <td>Bankrekening</td>
                      <td className="list-stamp no-wrap">{getTranslation('import_LastUpdated')}</td>
                      <td className="list-update no-wrap">{getTranslation('import_Import')}</td>
                      <td className="edit"></td>
                    </tr>
                    {imports.map((imp, index) => (
                      <React.Fragment key={imp.id}>
                        <tr value={index}>
                          <td className="td-expand">
                            {imp.impid > 0 && (
                              <div className="config-expand-btn hover" onClick={() => toggleConfigFiles(imp)}>
                                <FontAwesomeIcon
                                  icon={faChevronDown}
                                  className={`config-expand-icon${expandedConfigId === imp.id ? ' rotated' : ''}`}
                                />
                              </div>
                            )}
                          </td>
                          <td>{getCaseName(imp.entiteit)}</td>
                          <td>{imp.year}</td>
                          <td>{imp.jrnid}</td>
                          <td>{imp.accid}</td>
                          <td>{formatStamp(imp.stamp)}</td>
                          <td className="td-update">
                            <button className="btn-list hover" onClick={() => openUpload(imp)}>
                              {getTranslation('import_Update')}
                            </button>
                          </td>
                          <td className="td-editor">
                            <div className="delete-list list-editor-option hover" onClick={() => setConfirmDeleteId(imp.id)}>
                              <FontAwesomeIcon icon={faEllipsisV} />
                            </div>
                            {confirmDeleteId === imp.id && (
                              <ConfirmationComponent
                                message="Weet u zeker dat u deze configuratie wilt verwijderen?"
                                onCancel={() => setConfirmDeleteId(false)}
                                onConfirm={() => handleDelete(imp.id)}
                              />
                            )}
                          </td>
                        </tr>
                        {expandedConfigId === imp.id && (
                          <tr className="config-files-row">
                            <td colSpan={8}>
                              {(configFiles[imp.id] ?? []).length === 0 ? (
                                <p className="config-files-empty">Geen bestanden gevonden</p>
                              ) : (
                                <table className="config-files-table">
                                  <thead>
                                    <tr>
                                      <th>Bestandsnaam</th>
                                      <th>Type</th>
                                      <th>IBAN</th>
                                      <th>Periode</th>
                                      <th>Transacties</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(configFiles[imp.id] ?? []).map(f => (
                                      <tr key={f.id}>
                                        <td>{f.file_name}</td>
                                        <td>{f.file_type}</td>
                                        <td>{f.iban || '-'}</td>
                                        <td>{f.opening_balance_date && f.closing_balance_date ? `${f.opening_balance_date} – ${f.closing_balance_date}` : '-'}</td>
                                        <td>{f.transaction_count}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* ── Upload panel ── */}
            {activeUpload && (
              <div className="import-create-basic" style={{ marginTop: '20px' }}>
                <p style={{ fontWeight: 600 }}>
                  Bestanden uploaden naar: {getCaseName(activeUpload.config.entiteit)} · {activeUpload.config.year} · {activeUpload.config.jrnid} / {activeUpload.config.accid}
                </p>
                <LoaderComponent isLoading={activeUpload.isLoading} paddingTop="10px" />
                {!activeUpload.isLoading && (
                  <>
                    <FileDropComponent onFilesAccepted={files => setActiveUpload(u => ({ ...u, files }))} />
                    <div className="import-menubar">
                      <button className="import-btn submit-btn hover" disabled={!canImport} onClick={runImport}>
                        Importeren
                      </button>
                      <button className="import-btn cancel-btn hover" onClick={closeUpload}>
                        {getTranslation('import_Cancel')}
                      </button>
                    </div>
                    {activeUpload.results.length > 0 && (
                      <div className="import-results-wrapper">
                        <div className="import-results-header" onClick={() => setResultsExpanded(v => !v)}>
                          <span>Resultaten ({activeUpload.results.length})</span>
                          <FontAwesomeIcon
                            icon={faChevronDown}
                            className={`import-results-chevron${resultsExpanded ? ' rotated' : ''}`}
                          />
                        </div>
                        {resultsExpanded && (
                          <table className="import-results-table">
                            <thead>
                              <tr>
                                <th></th>
                                <th>Bestand</th>
                                <th>Details</th>
                              </tr>
                            </thead>
                            <tbody>
                              {activeUpload.results.map((r, i) => (
                                <tr key={i} className={r.success ? 'result-success' : 'result-error'}>
                                  <td><FontAwesomeIcon icon={r.success ? faCircleCheck : faCircleXmark} /></td>
                                  <td>{r.file}</td>
                                  <td>{r.success ? `${r.transactionCount} transacties · ${r.fileType}` : r.error}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default BestandComponent;
